import { Link } from 'react-router-dom';
import { useSEO, SITE_URL } from '@/lib/seo';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter, CONTACT_EMAIL } from '@/components/SiteFooter';
import { Breadcrumbs } from '@/components/Breadcrumbs';

const Section = ({ n, title, children }) => (
  <section className="mb-9">
    <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1C1917] mb-3">
      {n}. {title}
    </h2>
    <div className="font-['Outfit'] text-[#1C1917]/80 leading-relaxed space-y-3 text-[15px]">
      {children}
    </div>
  </section>
);

export default function Terms() {
  useSEO({
    title: 'Terms of Use | Masala Spin',
    description:
      'The terms that apply when you use Masala Spin — recipe and nutrition information, user submissions, copyright, external links, AI suggestions and liability.',
    canonical: `${SITE_URL}/terms`,
  });

  return (
    <div className="min-h-screen" style={{ background: '#FDFBF7' }}>
      <SiteHeader />
      <main className="max-w-3xl mx-auto px-6 py-14">
        <Breadcrumbs items={[{ name: 'Terms of Use' }]} />

        <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#1C1917] tracking-tight mb-3">
          Terms of Use
        </h1>
        <p className="font-['Outfit'] text-sm text-[#1C1917]/55 mb-10">Last updated: 14 June 2026</p>

        <div className="font-['Outfit'] text-[#1C1917]/80 leading-relaxed mb-10 text-[15px]">
          <p>
            These terms apply to your use of masalaspin.com ("Masala Spin", "the site", "we", "us").
            Masala Spin is an independently run recipe website. By using the site you accept these
            terms. If you do not agree with them, please stop using the site.
          </p>
        </div>

        <Section n="1" title="Using the website">
          <p>
            The site is free to use and does not require an account. You may browse, cook from and
            share our recipes for your own personal, non-commercial use.
          </p>
          <p>
            We may add, change or remove features, recipes and articles at any time, and we may
            interrupt access for maintenance. We do not promise that the site will always be
            available or free of errors.
          </p>
        </Section>

        <Section n="2" title="Recipe and informational content">
          <p>
            Recipes, cooking times, ingredient lists and articles are published for general
            information. They describe how a dish is commonly made; they are not professional
            culinary instruction and they have not been independently tested in a test kitchen.
          </p>
          <p>
            Many of our recipes list ingredients without measured quantities. You are responsible for
            judging quantities, seasoning, cooking temperatures and doneness — particularly for meat,
            fish, eggs and pulses, which must be cooked properly to be safe. You are also responsible
            for checking ingredient labels for allergens.
          </p>
        </Section>

        <Section n="3" title="Nutrition information">
          <p>
            Calorie figures shown on recipe pages are rough estimates for a single serving of the
            dish as described, and are labelled as estimates on the site. They are not laboratory
            analyses. Actual values depend on your portion size, brands, substitutions and how much
            fat you cook with.
          </p>
          <p>
            Dietary labels such as "vegan", "vegetarian", "gluten-free", "low-carb" or
            "diabetic-friendly" are broad descriptions of the dish, not certifications. Always check
            the products you buy.
          </p>
          <p className="font-semibold text-[#1C1917]">
            Nothing on Masala Spin is medical, nutritional or dietary advice. Meal plans and health
            or weight-related content are for general informational and educational purposes only.
            Consult a qualified healthcare professional or a registered dietitian before making
            significant changes to your diet, especially if you have a medical condition, take
            medication, or are pregnant or breastfeeding.
          </p>
        </Section>

        <Section n="4" title="User-submitted content">
          <p>
            If you send us a recipe, correction, message or other material through a form on the site
            or by email, you confirm that it is yours to share and that sharing it does not infringe
            anyone else's rights. You give us permission to publish, edit, adapt or decline to
            publish it, with credit where appropriate.
          </p>
          <p>
            Please do not submit confidential information, or content that is unlawful, misleading,
            offensive, or copied from someone else. We may remove submissions at our discretion.
          </p>
        </Section>

        <Section n="5" title="Intellectual property and copyright">
          <p>
            The site's name, layout, written articles and images are owned by Masala Spin or used
            with permission, unless stated otherwise. You may not republish substantial parts of the
            site, scrape it in bulk, or present our content as your own.
          </p>
          <p>
            Recipes themselves are traditional dishes and no one owns the idea of a dish — but our
            specific wording, article text and photography are protected.
          </p>
          <p>
            If you believe something on Masala Spin infringes your copyright, email{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#F97316] hover:underline break-all">
              {CONTACT_EMAIL}
            </a>{' '}
            with the URL, a description of the work and your contact details, and we will review and
            respond.
          </p>
        </Section>

        <Section n="6" title="External links">
          <p>
            We link to other websites where it is useful. We do not control those sites and are not
            responsible for their content, accuracy or privacy practices. Following an external link
            is at your own risk.
          </p>
        </Section>

        <Section n="7" title="Interactive tools">
          <p>
            The recipe wheel, custom wheel builder and meal planner are convenience tools. The wheel
            selects randomly from the recipes matching your filters and does not consider your
            health, allergies or dietary requirements. Meal planner data is stored in your own
            browser, so clearing your browser data will remove it.
          </p>
        </Section>

        <Section n="8" title="AI-generated suggestions">
          <p>
            The chef bot uses a third-party AI language model to suggest dishes based on the
            ingredients you type in. These suggestions are generated automatically and are not
            reviewed by us before you see them. They can be inaccurate, incomplete or unsuitable for
            your diet, and they are not a substitute for a tested recipe. Use your own judgement,
            especially around food safety and allergens.
          </p>
        </Section>

        <Section n="9" title="Advertising">
          <p>
            The site is funded by advertising and displays ads served by Google AdSense. We do not
            control which ads appear, and showing an ad is not an endorsement of the advertiser. See
            our <Link to="/privacy-policy" className="text-[#F97316] hover:underline">privacy policy</Link>{' '}
            for how advertising cookies work.
          </p>
        </Section>

        <Section n="10" title="Acceptable use">
          <p>You agree not to:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>interfere with the site's operation or security, or attempt to access parts of it that are not public;</li>
            <li>use automated tools to scrape, copy or overload the site or its API;</li>
            <li>click your own or others' ads artificially, or otherwise interfere with advertising;</li>
            <li>submit spam, malware or unlawful content through our forms;</li>
            <li>misrepresent your identity when contacting us.</li>
          </ul>
        </Section>

        <Section n="11" title="Limitation of liability">
          <p>
            The site is provided "as is". To the fullest extent permitted by law, we are not liable
            for any loss or damage arising from your use of the site or reliance on its content,
            including cooking outcomes, dietary decisions, food spoilage or allergic reactions.
          </p>
          <p>
            Nothing in these terms limits liability that cannot legally be limited.
          </p>
        </Section>

        <Section n="12" title="Changes to these terms">
          <p>
            We may update these terms as the site changes. The current version is always on this
            page, with the "last updated" date above. Continuing to use the site after a change means
            you accept the updated terms.
          </p>
        </Section>

        <Section n="13" title="Contact">
          <p>
            Questions about these terms? Email{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#F97316] hover:underline break-all">
              {CONTACT_EMAIL}
            </a>{' '}
            or use the{' '}
            <Link to="/contact" className="text-[#F97316] hover:underline">contact page</Link>.
          </p>
        </Section>
      </main>
      <SiteFooter />
    </div>
  );
}
